import { RouterState } from 'react-router';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withFetch } from '@flowio/redux-fetch';
import { setOverlayProps } from '@flowio/redux-form-overlay';
import { submit, getFormSyncErrors } from 'redux-form';

import moment from 'moment';
import OrderDetails from '../components/order-details';
import { fetchOrganization, getOrganization } from '../../organization';
import {
  getOrderDetail,
  getOrderSettings,
  getNotificationMessage,
  getRefundDialogState,
  getRefundOrderSummary,
  getApproveFraudReviewDialogState,
  getDeclineFraudReviewDialogState,
  getBusinessInvoices,
  getConsumerInvoices,
  getCreditMemos,
  getFailedInvoiceTypes,
  getInvoicesError,
  getOriginalDestination,
  getOrderTrackingDetails,
  getAllocations,
  getShippingNotifications,
  getBusinessCreditMemos,
  getCustomerServiceState,
  getDebugTransactions,
  getFeeInvoices,
  getBusinessDebitNotes,
  getBusinessCreditNotes,
} from '../selectors';
import fetchOrderDetailByNumber from '../actions/fetch-order-detail-by-number';
import fetchOrderSettings from '../actions/fetch-order-settings';
import fetchRefundOrderSummary from '../actions/fetch-refund-order-summary';
import getUserEmail from '../../console/selectors/get-user-email';
import hideRefundFormDialog from '../actions/hide-refund-form-dialog';
import hideApproveFraudReviewDialog from '../actions/hide-approve-fraud-review-dialog';
import showApproveFraudReviewDialog from '../actions/show-approve-fraud-review-dialog';
import hideDeclineFraudReviewDialog from '../actions/hide-decline-fraud-review-dialog';
import showDeclineFraudReviewDialog from '../actions/show-decline-fraud-review-dialog';
import showOrderTrackingDetailsDialog from '../actions/show-order-tracking-detail-dialog';
import hideOrderTrackingDetailsDialog from '../actions/hide-order-tracking-detail-dialog';
import resetNotification from '../actions/reset-notification';
import resetRefundOrderSummary from '../actions/reset-refund-order-summary';
import showRefundFormDialog from '../actions/show-refund-form-dialog';
import createFraudReviewDecision from '../actions/create-fraud-review-decision';
import { fetchCurrencies } from '../../reference';
import fetchInvoices from '../actions/fetch-invoices';
import FormNames from '../constants/FormNames';
import fetchAllocations from '../actions/fetch-allocations';
import { getIsCustomerServiceUser, getIsFlowEmployee } from '../../user';
import { createToast } from '../../console/actions';
import { RootState, ThunkDispatcher } from '../../../stores/types';
import {
  OrderDetailStateProps,
  OrderDetailDispatchProps,
  OrderDetailProps,
} from '../types';
import fetchShippingNotifications from '../actions/fetchShippingNotifications';
import { fetchFeatures } from '../../features/actions';
import { checkFeatureValueByKey } from '../../console';
import { FeatureKeys } from '../../console/constants';
import { fetchAddressValidationUser, fetchOrderAddressValidation } from '../actions';
import fetchDebugTransactions from '../actions/fetch-debug-transactions';

const refundFormErrorSelector = getFormSyncErrors(FormNames.ORDER_REFUND_FORM);
const getAsyncState = (
  dispatch: ThunkDispatcher,
  _getState: () => RootState,
  { params }: RouterState,
): Promise<void> => {
  dispatch(resetNotification());
  dispatch(resetRefundOrderSummary());
  return Promise.all([
    dispatch(fetchOrganization(params.organization)),
    dispatch(fetchDebugTransactions({
      order_number: params.number,
      organization_id: params.organization,
    })),
    dispatch(fetchRefundOrderSummary(params.organization, params.number)),
    dispatch(fetchOrderDetailByNumber(params.organization, params.number)),
    dispatch(fetchOrderSettings(params.organization, params.number)),
    dispatch(fetchShippingNotifications({
      orderNumber: params.number,
      organizationId: params.organization,
    })),
    dispatch(fetchInvoices(params.organization, params.number)),
    // currencies used for order refund form currency rounding.
    dispatch(fetchCurrencies()),
    dispatch(fetchFeatures(params.organization)),
  ]).then(([,,, orderDetailsResponse]) => {
    const order = orderDetailsResponse ? orderDetailsResponse.order : undefined;
    return dispatch(fetchAllocations(order ? order.number : ''));
  });
};

const mapStateToProps = (state: RootState, ownProps: RouterState): OrderDetailStateProps => ({
  addressValidation: getCustomerServiceState(state).orderDetail.addressValidation,
  addressValidationUser: getCustomerServiceState(state).orderDetail.addressValidationUser,
  allocations: getAllocations(state) as io.flow.v0.models.AllocationV2,
  approveFraudReviewDialogState: getApproveFraudReviewDialogState(state),
  businessCreditMemos: getBusinessCreditMemos(state),
  businessInvoices: getBusinessInvoices(state),
  consumerInvoices: getConsumerInvoices(state),
  creditMemos: getCreditMemos(state),
  feeInvoices: getFeeInvoices(state),
  businessDebitNotes: getBusinessDebitNotes(state),
  businessCreditNotes: getBusinessCreditNotes(state),
  declineFraudReviewDialogState: getDeclineFraudReviewDialogState(state),
  debugTransactions: getDebugTransactions(state),
  failedInvoiceTypes: getFailedInvoiceTypes(state),
  invoicesError: getInvoicesError(state),
  isCustomerServiceUser: getIsCustomerServiceUser(state),
  isFlowEmployee: getIsFlowEmployee(state),
  isOrderAddressValidationEnabled: checkFeatureValueByKey(
    FeatureKeys.GLOBAL_LABEL_ADDRESS_VALIDATION,
  )(state),
  notificationMessage: getNotificationMessage(state),
  orderDetail: getOrderDetail(state),
  orderSettings: getOrderSettings(state),
  orderTrackingDetails: getOrderTrackingDetails(state),
  organization: getOrganization(state),
  originalShippingAddress: getOriginalDestination(state),
  refundFormErrors: refundFormErrorSelector(state),
  refundOrderSummary: getRefundOrderSummary(state),
  refundDialogState: getRefundDialogState(state),
  shippingNotifications: getShippingNotifications(
    state,
    ownProps.params.organization,
    ownProps.params.number,
  ),
  userEmail: getUserEmail(state),
});

const mapDispatchToProps = (dispatch: ThunkDispatcher): OrderDetailDispatchProps => ({
  onEditShippingAddressEdit: (
    shippingAddress: io.flow.v0.models.OrderAddress,
    organizationId: string,
    orderNumber: string,
  ): void => {
    dispatch(setOverlayProps(FormNames.EDIT_SHIPPING_ADDRESS_FORM, {
      initialValues: {
        organizationId,
        orderNumber,
        ...shippingAddress,
      },
      open: true,
      title: 'Update Shipping Address',
      saveButton: null,
      cancelButton: { content: 'Close', intent: 'neutral' },
    }));
  },
  onEditDispute: (
    { dispute, authorization }: io.flow.internal.v0.models.DisputeDetail,
  ): void => {
    dispatch(setOverlayProps(FormNames.EDIT_DISPUTE_FORM, {
      initialValues: {
        disputeDetails: {
          dispute: { ...dispute },
          authorization: { ...authorization },
        },
        id: dispute.id,
        // Set dropdown defaults to be current values in dispute
        billable: (dispute.billable || '').toUpperCase(),
        category: (dispute.category || '').toUpperCase(),
        defense_outcome: (dispute.defense_outcome || '').toUpperCase(),
        status: (dispute.status || '').toUpperCase(),
        defended_at: (dispute.defended_at == null) ? null : moment(dispute.defended_at).toDate(),
      },
      open: true,
      title: 'Update Dispute',
    }));
  },
  onRequestDismissNotificationMessage: (): void => {
    dispatch(resetNotification());
  },
  onRequestHideRefundFormDialog: (): void => {
    dispatch(hideRefundFormDialog());
  },
  onRequestShowRefundFormDialog: (): void => {
    dispatch(showRefundFormDialog());
  },
  onRequestShowOrderTrackingDetailsDialog: (
    orderNumber: string,
    label: io.flow.internal.v0.models.LabelSummary | undefined,
    notification: io.flow.v0.models.ShippingNotification | undefined,
  ): void => {
    dispatch(showOrderTrackingDetailsDialog(orderNumber, label, notification));
  },
  onRequestHideorderTrackingDetailsDialog: (): void => {
    dispatch(hideOrderTrackingDetailsDialog());
  },
  onRequestHideApproveFraudReviewDialog: (): void => {
    dispatch(hideApproveFraudReviewDialog());
  },
  onRequestShowApproveFraudReviewDialog: (fraudId: string): void => {
    dispatch(showApproveFraudReviewDialog(fraudId));
  },
  onRequestHideDeclineFraudReviewDialog: (): void => {
    dispatch(hideDeclineFraudReviewDialog());
  },
  onRequestShowDeclineFraudReviewDialog: (fraudId: string): void => {
    dispatch(showDeclineFraudReviewDialog(fraudId));
  },
  onSubmitRefund: (): void => {
    dispatch(submit(FormNames.ORDER_REFUND_FORM));
  },
  onCopyAuthorizationIdentifier: (orderNumber: string): void => {
    navigator.clipboard.writeText(orderNumber).then(() => {
      dispatch(createToast({
        intent: 'positive',
        content: 'Successfully copied Authorization Identifier.',
      }));
    }).catch(() => {
      dispatch(createToast({
        intent: 'negative',
        content: 'Could not copy Authorization Identifier.',
      }));
    });
  },
  onRequestOrganizationFraudReviewApprove: (organization: string, fraudReviewId: string): void => {
    const status: io.flow.v0.enums.FraudStatus = 'approved';

    const form = { status };
    dispatch(createFraudReviewDecision(
      organization,
      fraudReviewId,
      form,
    ));
  },
  onRequestOrganizationFraudReviewDecline: (organization: string, fraudReviewId: string): void => {
    const status: io.flow.v0.enums.FraudStatus = 'declined';
    const form = { status };
    dispatch(createFraudReviewDecision(
      organization,
      fraudReviewId,
      form,
    ));
  },
  onRequestOrderAddressValidation: (organizationId: string, orderNumber: string): void => {
    dispatch(fetchOrderAddressValidation(organizationId, orderNumber)).then((response) => {
      if (response.ok) {
        dispatch(fetchAddressValidationUser(response.body.updated_by_user_id));
      }
    });
  },
});

export default compose<React.FC<OrderDetailProps>>(
  withFetch(getAsyncState),
  connect(mapStateToProps, mapDispatchToProps),
)(OrderDetails);
